import type { LinkReply } from '@/api/generated';
import { linkStatusLabel } from '@/lib/linkStatus';

/*
 * A link as the sub-pages read it, and what follows from its state.
 *
 * The link is the server's; the page keeps nothing of its own beyond the
 * form. `toLinkView` is the one place the generated shape is read, so a field
 * rename is one edit (design.md "Domain model").
 */

/** Which header button the link stands behind, as the routes name it. */
export type LinkKind = 'fundraising' | 'volunteer';

export interface LinkView {
  kind: LinkKind;
  /** NOT_SET, DRAFT, PUBLISHED, or a code the portal has not met. */
  status: string;
  /** The draft address, or null when the link is not set. */
  url: string | null;
  /** The address the app opens, or null while unpublished. */
  publishedUrl: string | null;
  hasUnpublishedChanges: boolean;
  /** Clicks over `range` days; never donations (P6). */
  clicks: number;
  range: number;
  /** The volunteer link's mode; null on fundraising. */
  mode: string | null;
}

/**
 * The generated reply as a `LinkView`. A write's reply carries its clicks over
 * the default range, so it is mapped with the read's `range` (backend B11).
 */
export function toLinkView(link: LinkReply, range: number): LinkView {
  return {
    kind: link.kind.toLowerCase() as LinkKind,
    status: link.status,
    url: link.url ?? null,
    publishedUrl: link.published_url ?? null,
    hasUnpublishedChanges: link.has_unpublished_changes,
    clicks: link.clicks,
    range,
    mode: link.mode ?? null,
  };
}

/** The chip's word: Not set, "Saved, not published", Published, "Published · Unsaved changes", or the code (I1). */
export function statusWord(view: LinkView): string {
  if (view.status === 'PUBLISHED' && view.hasUnpublishedChanges) {
    return 'Published · Unsaved changes';
  }
  return linkStatusLabel(view.status);
}

/** Which write controls the page shows (I2). */
export interface Offers {
  save: boolean;
  publish: boolean;
  /**
   * A Published link with nothing unsaved: Publish Changes stays disabled
   * until the form is edited, so the page never sends the no-op publish
   * (B10, P18).
   */
  nothingToPublish: boolean;
  unpublish: boolean;
  remove: boolean;
}

/**
 * What a person may do to the link. Nothing for a read-only viewer (P20);
 * Unpublish only on Published, Remove link only on a saved, unpublished link with an address,
 * since taking a live button away is Unpublish's job (P4).
 */
export function offers(view: LinkView, writable: boolean): Offers {
  const published = view.status === 'PUBLISHED';
  return {
    save: writable,
    publish: writable,
    nothingToPublish: published && !view.hasUnpublishedChanges,
    unpublish: writable && published,
    remove: writable && view.status === 'DRAFT' && view.url !== null,
  };
}

/** "12 clicks · last 30 days", "1 click · last 30 days" (P6). */
export function clicksLine(clicks: number, range: number): string {
  return `${String(clicks)} ${clicks === 1 ? 'click' : 'clicks'} · last ${String(range)} days`;
}
